import { Box, CssBaseline } from "@mui/material";
import { useLocation, useNavigate } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import { trpc } from "~/utils/trpc";
import CreateProjectModal from "../CreateProjectModal";
import CreateTeamModal from "../CreateTeamModal";
import { useTeamProject } from "../TeamProjectProvider";
import { MainContent } from "./MainContent";
import { Sidebar } from "./Sidebar";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { data: user } = trpc.user.me.useQuery();
  const {
    selectedTeamId,
    setSelectedTeamId,
    selectedProjectId,
    setSelectedProjectId,
    teams,
  } = useTeamProject();

  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);

  /** Team settings ships its own nav column; avoid stacking two sidebars on desktop. */
  const normalizedPath = location.pathname.replace(/\/$/, "") || "/";
  const hideAppSidebar =
    normalizedPath === "/dashboard/team-settings" ||
    normalizedPath.startsWith("/dashboard/team-settings/");

  const selectedTeam = teams.find((t) => t.id === selectedTeamId);

  // Auto-prompt messenger connection logic
  const dismissedTeamsRef = useRef<Set<string>>(new Set());
  const prevTeamIdRef = useRef<string>(selectedTeamId);
  const hasTriggeredInitialRef = useRef(false);

  useEffect(() => {
    if (!selectedTeamId || !selectedTeam || !user) return;

    const isSwitch = selectedTeamId !== prevTeamIdRef.current;
    prevTeamIdRef.current = selectedTeamId;

    const shouldCheck = isSwitch || !hasTriggeredInitialRef.current;
    if (!shouldCheck) return;
    hasTriggeredInitialRef.current = true;

    // Owner with no messenger server → redirect to connect-messenger
    const isOwner = selectedTeam.ownerId === user.id;
    if (
      isOwner &&
      !selectedTeam.messengerServerId &&
      !dismissedTeamsRef.current.has(selectedTeamId)
    ) {
      const timer = setTimeout(() => {
        dismissedTeamsRef.current.add(selectedTeamId);
        navigate(`/connect-messenger?teamId=${selectedTeamId}`);
      }, 400);
      return () => clearTimeout(timer);
    }

    const selectedPlatform = selectedTeam.platformType;
    if (!selectedPlatform) return;

    const connectedPlatforms = new Set(
      user.messengerAccounts
        ?.filter((a) => a.externalId)
        .map((a) => a.platformType) ?? [],
    );
    const hasMissingPlatform = !connectedPlatforms.has(selectedPlatform);

    if (hasMissingPlatform && !dismissedTeamsRef.current.has(selectedTeamId)) {
      const timer = setTimeout(() => {
        dismissedTeamsRef.current.add(selectedTeamId);
        navigate(
          `/connect-messenger?teamId=${selectedTeamId}&platformType=${selectedPlatform}`,
        );
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [selectedTeamId, selectedTeam, user, navigate]);

  useEffect(() => {
    const handler = () => setMobileOpen(true);
    document.addEventListener("open-mobile-sidebar", handler);
    return () => document.removeEventListener("open-mobile-sidebar", handler);
  }, []);

  useEffect(() => {
    const handler = () => setMobileOpen(false);
    document.addEventListener("close-mobile-sidebar", handler);
    return () => document.removeEventListener("close-mobile-sidebar", handler);
  }, []);

  useEffect(() => {
    const handler = () => setCreateProjectOpen(true);
    document.addEventListener("open-create-project-modal", handler);
    return () =>
      document.removeEventListener("open-create-project-modal", handler);
  }, []);

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      {!hideAppSidebar && (
        <Sidebar
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
      )}

      <MainContent onMobileMenuOpen={() => setMobileOpen(true)}>
        {children}
      </MainContent>

      <CreateTeamModal
        open={createTeamOpen}
        onClose={() => setCreateTeamOpen(false)}
        onCreated={(teamId) => {
          setSelectedTeamId(teamId);
          setSelectedProjectId("");
          navigate(`/connect-messenger?teamId=${teamId}`);
        }}
      />

      {selectedTeamId && (
        <CreateProjectModal
          open={createProjectOpen}
          onClose={() => setCreateProjectOpen(false)}
          teamId={selectedTeamId}
          onCreated={(projectId) => {
            setSelectedProjectId(projectId);
            navigate("/dashboard");
          }}
        />
      )}
    </Box>
  );
}
